import React from "react";

import { WebContainerStatus } from "@/features/web-container/useWebContainerHook";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import twMerge from "@replo/design-system/utils/twMerge";
import { BadgeCheckIcon, LoaderIcon } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { useWebContainer } from "../../web-container/context";

const mapStatusToProgress = (status: WebContainerStatus) => {
  return exhaustiveSwitch({ type: status.toString() })({
    [WebContainerStatus.Booting.toString()]: 0,
    [WebContainerStatus.WaitingForFiles.toString()]: 5,
    [WebContainerStatus.Mounting.toString()]: 15,
    [WebContainerStatus.Installing.toString()]: 30,
    [WebContainerStatus.Starting.toString()]: 40,
    [WebContainerStatus.Downloading.toString()]: 50,
    [WebContainerStatus.Ready.toString()]: 60,
    [WebContainerStatus.Compiling.toString()]: 75,
    [WebContainerStatus.Serving.toString()]: 100,
    [WebContainerStatus.Error.toString()]: 100,
  });
};

// Linear Interpolation
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const useAnimatedProgress = (target: number, baseSpeed = 0.02) => {
  const [value, setValue] = React.useState(target);

  React.useEffect(() => {
    let frame: number;

    const animate = () => {
      setValue((v) => {
        // ---------- speed logic ----------
        // 1. Normal: creep at baseSpeed (e.g. 2 % of gap per frame)
        // 2. Finish: if target is 100, bump speed way up
        //            (feel free to fine-tune 0.18 → 0.25 etc.)
        const speed = target === 100 ? 0.18 : baseSpeed;

        const next = lerp(v, target, speed);

        // snap when we’re <0.5 % away to avoid interminable micro-steps
        if (Math.abs(next - target) < 0.5) {
          return target;
        }

        frame = requestAnimationFrame(animate);
        return next;
      });
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target, baseSpeed]);

  return value;
};

// inside your component:

const LoadingHeader: React.FC = () => {
  const { status } = useWebContainer();
  const progress = useAnimatedProgress(mapStatusToProgress(status));

  const [isHidden, setIsHidden] = React.useState(
    status === WebContainerStatus.Serving,
  );
  const isLoaded = status === WebContainerStatus.Serving;

  React.useEffect(() => {
    if (status !== WebContainerStatus.Serving) {
      setIsHidden(false);
    }
  }, [status]);

  React.useEffect(() => {
    if (isLoaded) {
      const timeout = setTimeout(() => {
        setIsHidden(true);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isLoaded]);

  return (
    <div
      className={twMerge(
        "absolute top-0 rounded-t left-0 right-0 overflow-hidden z-1",
        isHidden && "pointer-events-none",
      )}
    >
      <div
        className={twMerge(
          "bg-white pb-3 border-b border-border flex flex-col items-stretch gap-3",
          "transition-transform duration-300",
          isHidden && "-translate-y-full",
        )}
      >
        <Progress
          value={progress}
          indicatorClassName="bg-slate-800 transition"
          className="w-auto rounded-none bg-surface"
        />
        <div className="flex flex-row px-4 gap-2">
          <div
            className={twMerge(
              "flex flex-row items-center border-1 text-info border-info-border justify-center h-6 w-6 gap-2 bg-info-soft rounded-md",
              isLoaded && "text-success bg-success-soft border-success-border",
            )}
          >
            {isLoaded ? (
              <BadgeCheckIcon size={12} />
            ) : (
              <LoaderIcon
                size={12}
                className="animate-[spin_1.5s_linear_infinite]"
              />
            )}
          </div>
          <div>
            <div className="header-h4">
              {isLoaded ? "Good to go!" : "Loading in Your Page"}
            </div>
            <div className="text-base text-muted">
              {isLoaded
                ? "Your page is fully interactive and ready to edit."
                : "You'll be able to interact with your page soon."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingHeader;
